import React, { useState, useEffect, useMemo } from 'react'
import { toast } from 'react-toastify'
import { apiLabs, apiSantoLog } from '../../../Services/api'
import Dashboard from '../../../Components/Dashboard'
import { store } from '../../../store'
import history from '../../../Services/history'
import Denied from '../../../Components/Denied'
import { printDrivers, printReturnRomaneio } from '../_shared/printRomaneio'
import {
  GlobalRomaneioStyles, PageWrap, Topbar, Body, Toolbar,
  Field, DateInput, Select, Stats, RefreshBtn, PrintBtn,
  Content, DriverCard, CardHead, Kpis,
  Table, THead, HRow, Row, Empty, Loading,
} from './style'

// ─── HELPERS ──────────────────────────────────────────────────────────────────
// Data de hoje no horário LOCAL (sem toISOString, que converteria pra UTC).
function todayLocalISO() {
  const d = new Date()
  const p = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}
function fmtDateBR(iso) {
  const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!m) return String(iso || '—')
  return `${m[3]}/${m[2]}/${m[1]}`
}
function fmtPeso(p) {
  if (p == null || p === '') return '—'
  const n = Number(p)
  if (Number.isNaN(n)) return String(p)
  return n.toFixed(2).replace('.', ',')
}
// Endereço de entrega compacto: "Rua, complemento — Bairro - Cidade/UF".
function formatAddress(order) {
  const a = order.address || order || {}
  const line1 = [a.street, a.complement].filter(Boolean).join(', ')
  const line2 = [a.neighborhood, [a.city, a.state_uf].filter(Boolean).join('/')].filter(Boolean).join(' - ')
  const full = [line1, line2].filter(Boolean).join(' — ')
  return full || '—'
}
function fmtPhone(raw) {
  const d = String(raw || '').replace(/\D/g, '')
  if (d.length === 11) return d.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3')
  if (d.length === 10) return d.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3')
  return raw ? String(raw) : '—'
}
function getPhone(order) {
  const raw =
    order.recipient_phone ?? order.phone ?? order.telefone ??
    order.recipient_telephone ?? order.cellphone ?? null
  return raw ? fmtPhone(raw) : '—'
}
function nfFromOrder(order) {
  const vols = Array.isArray(order.volumes) ? order.volumes : []
  const nfs = [...new Set(vols.map((v) => v.nfe_number).filter(Boolean))]
  if (nfs.length) return nfs.join(', ')
  return order.order_number || '—'
}
function volumesCount(order) {
  if (order.volumes_count != null) return order.volumes_count
  return Array.isArray(order.volumes) ? order.volumes.length : 0
}
function driverKey(block, i) {
  return block?.driver?.id || `no-driver-${i}`
}

const TABS = [
  { key: 'embarcados', label: '📦 Embarcados' },
  { key: 'nao_embarcados', label: '↩ Não Embarcados' },
]

// ─── COMPONENT ────────────────────────────────────────────────────────────────
export default function SantoLogRomaneio(props) {
  const { location: { idPage } } = props
  const [auth, setAuth] = useState(null)

  async function verifyAuth() {
    const { profile } = store.getState().user
    const res = await apiLabs.post('valid_key', { id: profile.id, menu_id: idPage })
    if (res.data.message === 'User not authorized') return setAuth('denied')
    if (res.data.message === 'User authorized') return setAuth('authorized')
    return history.push({ pathname: '/', state: { from: props.location } })
  }

  const [tab, setTab] = useState('embarcados') // 'embarcados' | 'nao_embarcados'
  const [date, setDate] = useState(todayLocalISO())
  const [payload, setPayload] = useState({ date: todayLocalISO(), drivers: [], totals: {} })  // embarcados
  const [naoEmb, setNaoEmb] = useState({ date: todayLocalISO(), items: [], totals: {} })       // não-embarcados
  const [selected, setSelected] = useState('all') // 'all' | driverKey
  const [loading, setLoading] = useState(false)
  const [expanded, setExpanded] = useState(() => new Set())

  function toggleDriver(key) {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key); else next.add(key)
      return next
    })
  }

  // ── INIT / FETCH ────────────────────────────────────────────────────────────
  useEffect(() => { verifyAuth() }, []) // eslint-disable-line
  useEffect(() => {
    if (auth !== 'authorized') return
    if (tab === 'embarcados') fetchEmbarcados(date)
    else fetchNaoEmbarcados(date)
  }, [auth, date, tab]) // eslint-disable-line

  async function fetchEmbarcados(d) {
    setLoading(true)
    try {
      const res = await apiSantoLog.get('/romaneio/embarcado', { params: { date: d } })
      const data = res.data || { date: d, drivers: [], totals: {} }
      setPayload(data)
      setSelected('all')
      setExpanded(new Set())
    } catch (err) {
      console.error('GET /romaneio/embarcado failed:', err)
      toast.error('Erro ao carregar embarcados: ' + (err?.response?.data?.error || err?.response?.data?.message || err.message))
      setPayload({ date: d, drivers: [], totals: {} })
    } finally {
      setLoading(false)
    }
  }

  async function fetchNaoEmbarcados(d) {
    setLoading(true)
    try {
      const res = await apiSantoLog.get('/romaneio/nao-embarcado', { params: { date: d } })
      const data = res.data || { date: d, items: [], totals: {} }
      setNaoEmb(data)
    } catch (err) {
      console.error('GET /romaneio/nao-embarcado failed:', err)
      toast.error('Erro ao carregar não-embarcados: ' + (err?.response?.data?.error || err?.response?.data?.message || err.message))
      setNaoEmb({ date: d, items: [], totals: {} })
    } finally {
      setLoading(false)
    }
  }

  function refresh() {
    if (tab === 'embarcados') fetchEmbarcados(date)
    else fetchNaoEmbarcados(date)
  }

  const drivers = useMemo(
    () => (Array.isArray(payload.drivers) ? payload.drivers : []),
    [payload]
  )
  const visibleDrivers = useMemo(() => {
    if (selected === 'all') return drivers
    return drivers.filter((b, i) => driverKey(b, i) === selected)
  }, [drivers, selected])

  const naoItems = Array.isArray(naoEmb.items) ? naoEmb.items : []
  const naoTotals = naoEmb.totals || {}

  // ── IMPRESSÃO ─────────────────────────────────────────────────────────────
  function handlePrint(blocks) {
    const list = Array.isArray(blocks) ? blocks : [blocks]
    if (!list.length) {
      toast.warn('Nenhum motorista para imprimir.')
      return
    }
    const { ok, error } = printDrivers({ date: payload.date || date, drivers: list })
    if (!ok && error === 'popup_blocked') {
      toast.warn('Permita pop-ups neste site para imprimir o romaneio.')
    }
  }

  function handlePrintNaoEmb() {
    if (!naoItems.length) {
      toast.warn('Nenhum não-embarcado para imprimir.')
      return
    }
    const { ok, error } = printReturnRomaneio(
      { date: naoEmb.date || date, items: naoItems, totals: naoTotals },
      { title: 'Romaneio de Não-Embarcados' }
    )
    if (!ok && error === 'popup_blocked') {
      toast.warn('Permita pop-ups neste site para imprimir o romaneio.')
    }
  }

  const totalDrivers = drivers.length
  const totalOrders = payload?.totals?.orders ?? drivers.reduce((s, b) => s + (b?.totals?.orders || 0), 0)
  const totalVolumes = payload?.totals?.volumes ?? drivers.reduce((s, b) => s + (b?.totals?.volumes || 0), 0)

  // ── RENDER ────────────────────────────────────────────────────────────────
  return (
    <Dashboard title="Romaneio">
      {auth === 'denied' && <Denied />}
      {auth === 'authorized' && (
        <>
          <GlobalRomaneioStyles />
          <PageWrap>
            <Topbar>
              <h2>🧾 Romaneios</h2>
              <span className="crumb">Histórico por data · embarcados e não-embarcados · pronto para impressão</span>
            </Topbar>

            <Body>
              {/* Abas */}
              <div style={{ display: 'flex', gap: 6, padding: '0 16px', marginTop: 12 }}>
                {TABS.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setTab(t.key)}
                    disabled={loading}
                    style={{
                      padding: '10px 18px', borderRadius: '8px 8px 0 0', cursor: 'pointer',
                      fontSize: 13, fontWeight: 700, fontFamily: "'DM Sans',sans-serif",
                      border: '1px solid', borderBottom: 'none',
                      borderColor: tab === t.key ? '#2a3040' : 'transparent',
                      background: tab === t.key ? '#181c23' : 'transparent',
                      color: tab === t.key ? '#00e5a0' : '#8a95a3',
                    }}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              <Toolbar>
                <Field>
                  Data
                  <DateInput
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                  />
                </Field>

                {tab === 'embarcados' && (
                  <Field>
                    Motorista
                    <Select value={selected} onChange={(e) => setSelected(e.target.value)}>
                      <option value="all">Todos os motoristas</option>
                      {drivers.map((b, i) => (
                        <option key={driverKey(b, i)} value={driverKey(b, i)}>
                          {(b.driver?.name || 'Sem motorista')}
                          {b.totals ? ` · ${b.totals.orders || 0} ped. / ${b.totals.volumes || 0} vol.` : ''}
                        </option>
                      ))}
                    </Select>
                  </Field>
                )}

                <RefreshBtn onClick={refresh} disabled={loading}>
                  {loading ? '…' : '↻ Atualizar'}
                </RefreshBtn>

                {tab === 'embarcados' ? (
                  <>
                    <Stats>
                      <span><strong>{totalDrivers}</strong>motoristas</span>
                      <span><strong>{totalOrders}</strong>pedidos</span>
                      <span><strong>{totalVolumes}</strong>volumes</span>
                    </Stats>
                    <PrintBtn onClick={() => handlePrint(visibleDrivers)} disabled={loading || !visibleDrivers.length}>
                      🖨 Imprimir {selected === 'all' ? 'todos' : 'romaneio'}
                    </PrintBtn>
                  </>
                ) : (
                  <>
                    <Stats>
                      <span><strong>{naoTotals.pedidos ?? naoItems.length}</strong>pedidos</span>
                      <span><strong>{naoTotals.volumes ?? '—'}</strong>volumes</span>
                      <span><strong>{fmtPeso(naoTotals.peso)}</strong>kg</span>
                    </Stats>
                    <PrintBtn onClick={handlePrintNaoEmb} disabled={loading || !naoItems.length}>
                      🖨 Imprimir
                    </PrintBtn>
                  </>
                )}
              </Toolbar>

              <Content>
                {loading && (
                  <Loading>Carregando…</Loading>
                )}

                {/* ABA EMBARCADOS */}
                {!loading && tab === 'embarcados' && (
                  <>
                    {!drivers.length && (
                      <Empty>
                        Nenhum embarque encontrado para <strong>{fmtDateBR(date)}</strong>.<br />
                        Selecione outra data ou atualize.
                      </Empty>
                    )}
                    {visibleDrivers.map((block, i) => {
                      const driver = block.driver || {}
                      const totals = block.totals || {}
                      const orders = Array.isArray(block.orders) ? block.orders : []
                      const key = driverKey(block, i)
                      const open = expanded.has(key) || selected !== 'all'
                      return (
                        <DriverCard key={key}>
                          <CardHead onClick={() => toggleDriver(key)}>
                            <span className="chevron">{open ? '−' : '+'}</span>
                            <div className="who">
                              <span className="name">🚛 {driver.name || 'Sem motorista'}</span>
                            </div>
                            <Kpis>
                              <div className="kpi"><span>Pedidos</span><strong>{totals.orders ?? orders.length}</strong></div>
                              <div className="kpi"><span>Volumes</span><strong>{totals.volumes ?? '—'}</strong></div>
                            </Kpis>
                            <div className="print">
                              <PrintBtn onClick={(e) => { e.stopPropagation(); handlePrint(block) }}>🖨 Imprimir</PrintBtn>
                            </div>
                          </CardHead>

                          {open && (
                            <Table>
                              <THead>
                                <HRow>
                                  <div className="c">Seq.</div>
                                  <div>Nota Fiscal</div>
                                  <div>Endereço</div>
                                  <div>Telefone</div>
                                  <div className="c">Volumes</div>
                                </HRow>
                              </THead>
                              {orders.length === 0 ? (
                                <Row>
                                  <div className="c">—</div>
                                  <div style={{ color: '#5a6370' }}>Nenhum pedido embarcado.</div>
                                  <div /><div /><div />
                                </Row>
                              ) : (
                                orders.map((o, j) => (
                                  <Row key={o.id || `${key}-${j}`}>
                                    <div className="c">{o.sequence_order != null ? o.sequence_order : '—'}</div>
                                    <div className="mono">{nfFromOrder(o)}</div>
                                    <div>{formatAddress(o)}</div>
                                    <div className="mono">{getPhone(o)}</div>
                                    <div className="c mono">{volumesCount(o)}</div>
                                  </Row>
                                ))
                              )}
                            </Table>
                          )}
                        </DriverCard>
                      )
                    })}
                  </>
                )}

                {/* ABA NÃO EMBARCADOS */}
                {!loading && tab === 'nao_embarcados' && (
                  !naoItems.length ? (
                    <Empty>
                      Nenhum não-embarcado encontrado para <strong>{fmtDateBR(date)}</strong>.<br />
                      Selecione outra data ou atualize.
                    </Empty>
                  ) : (
                    <div style={{
                      background: '#111318', border: '1px solid #1f2430', borderRadius: 10,
                      overflow: 'hidden', margin: '0 16px 16px',
                    }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: "'DM Sans',sans-serif" }}>
                        <thead>
                          <tr style={{ background: '#181c23' }}>
                            {['#', 'Nota Fiscal', 'Peso (kg)', 'Volumes'].map((h, i) => (
                              <th key={h} style={{
                                textAlign: i === 2 ? 'right' : (i === 0 || i === 3 ? 'center' : 'left'),
                                padding: '10px 14px', fontSize: 10, fontWeight: 700, color: '#8a95a3',
                                textTransform: 'uppercase', letterSpacing: '.05em', borderBottom: '1px solid #2a3040',
                              }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {naoItems.map((it, i) => (
                            <tr key={it.pedido || it.linha || i} style={{ borderBottom: '1px solid #1a1e27' }}>
                              <td style={{ textAlign: 'center', padding: '9px 14px', fontSize: 12, color: '#8a95a3', fontFamily: "'Space Mono',monospace" }}>{it.linha != null ? it.linha : i + 1}</td>
                              <td style={{ padding: '9px 14px', fontSize: 12.5, color: '#e8ecf0', fontFamily: "'Space Mono',monospace", fontWeight: 700 }}>{it.nf || it.pedido || '—'}</td>
                              <td style={{ textAlign: 'right', padding: '9px 14px', fontSize: 12.5, color: '#cbd5e1', fontFamily: "'Space Mono',monospace" }}>{fmtPeso(it.peso)}</td>
                              <td style={{ textAlign: 'center', padding: '9px 14px', fontSize: 12.5, color: '#cbd5e1', fontFamily: "'Space Mono',monospace" }}>{it.volumes != null ? it.volumes : (Array.isArray(it.volumes_list) ? it.volumes_list.length : '—')}</td>
                            </tr>
                          ))}
                          <tr style={{ background: '#181c23', fontWeight: 700 }}>
                            <td colSpan={2} style={{ textAlign: 'center', padding: '10px 14px', fontSize: 12, color: '#e8ecf0' }}>
                              Totais — {naoTotals.pedidos ?? naoItems.length} pedido(s)
                            </td>
                            <td style={{ textAlign: 'right', padding: '10px 14px', fontSize: 12.5, color: '#e8ecf0', fontFamily: "'Space Mono',monospace" }}>{fmtPeso(naoTotals.peso)}</td>
                            <td style={{ textAlign: 'center', padding: '10px 14px', fontSize: 12.5, color: '#e8ecf0', fontFamily: "'Space Mono',monospace" }}>{naoTotals.volumes ?? '—'}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )
                )}
              </Content>
            </Body>
          </PageWrap>
        </>
      )}
    </Dashboard>
  )
}
